import { useNode } from "@craftjs/core";
import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import {
  goToUrl,
  percentageToSlider,
  sliderToPercentage,
} from "../../../../utils/helpers";
import CustomSwitch from "../../../CustomSwitch";
import Input from "../../../Input";
import Tabs from "../../../Tabs";
import TextView from "../../../TextView";
import ColorPopup from "../../../SegmentBuilder/Shared/ColorPopup";
import MarginBox from "../../Shared/MarginBox";
import SettingsSlider from "../../Shared/SettingsSlider";
import EmbedURL from "../../../SegmentBuilder/Shared/EmbedURL";
import UploadedMedia from "../../Shared/UploadedMedia";
import UploadMedia from "../../Shared/UploadMedia";
import MediaBrowser from "../../../SegmentBuilder/Shared/MediaBrowser";
import ShareWithBorder from "../../../../assets/icons/ShareWithBorder";
import RecentMedia from "../../Shared/RecentMedia";
import { useDispatch, useSelector } from "react-redux";
import { updateVideoStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { FEATURES } from "../../../../constants/features";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import TierFeatureBadge from "../../../TierFeatureBadge";
import { MEDIA_RECORDER_ENABLED } from "../../../../constants/featureFlags";
import MediaRecorder, { canRecordMedia } from "../../../MediaCapture";
import MediaLibraryReuse from "../../../MediaLibrary/MediaLibraryReuse";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import FeatureEligibility from "../../../FeatureEligibility";
import {
  applyVideoMediaProps,
  writeVideoMediaToOwningSegment,
} from "./writeVideoMedia";
import { getActiveUpload } from "../../Shared/UploadMedia/activeUploads";
import {
  orderSourceTabs,
  SOURCE_STRIP_SX,
} from "../../Shared/MediaSelection/sourceTabOrder";
import { CircularProgress } from "@mui/material";

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.lightBorder};
  margin-bottom: 20px;
`;

const VideoSettings = () => {
  const {
    id: nodeId,
    actions: { setProp },
    width,
    isApplyMarginOnAllSides,
    margin,
    allSideMargin,
    borderRadius,
    borderColor,
    borderWidth,
    src,
    title,
    showVideoTitle,
    hyperlink,
    showHyperlink,
    dropShadowColor,
  } = useNode((node) => ({
    width: node.data.props.width,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
    borderRadius: node.data.props.borderRadius,
    borderWidth: node.data.props.borderWidth,
    borderColor: node.data.props.borderColor,
    src: node.data.props.src,
    title: node.data.props.title,
    showVideoTitle: node.data.props.showVideoTitle,
    hyperlink: node.data.props.hyperlink,
    showHyperlink: node.data.props.showHyperlink,
    dropShadowColor: node.data.props.dropShadowColor,
    isLoading: node.data.props.isLoading,
  }));

  const dispatch = useDispatch();
  const { video: videoProps } = useSelector((state) => state.codeblockStyles);
  // DL-048 adoption: the Video block reuses videos only; name the open Segment.
  const selectedSegment = useSelector(getSelectedSegment);
  const segmentName = selectedSegment?.settings?.title || null;

  const mainTabs = ["Video Selection", "Video Layout"];
  const [selectedTab, setSelectedTab] = useState(mainTabs[0]);
  // T22.146 (Gordon 2026-10-12): the Video block strip follows the ONE
  // universal source order every other media picker uses — Upload · Embed
  // URL · Browse · Preset · Recent, then Record — via orderSourceTabs.
  // gordon-0915-video-block-media-pane-pixabay-embed-first-record-tier:
  // Embed URL stays the default-OPEN tab — it is the only source immediately
  // available to every tier (no upload/record entitlement needed); Upload and
  // Record each carry an (E) badge. "Record" is appended only when the flag
  // is ON *and* this browser/device can actually record an uploadable clip
  // (canRecordMedia) — so OFF is byte-identical, and unsupported devices
  // (Firefox, old browsers, no camera API) get Upload only instead of a
  // dead-end tab.
  const categories = orderSourceTabs([
    "Embed URL",
    "Browse",
    "Recent",
    "Upload",
    ...(MEDIA_RECORDER_ENABLED && canRecordMedia("video") ? ["Record"] : []),
  ]);
  // T22.143 (Kelly 2026-10-12): clicking another block unmounts this pane
  // mid-upload. Coming back to THIS block must show its running upload, so
  // the pane reopens on Upload when one is still in flight for this block.
  const uploadKey = `${selectedSegment?.id ?? ""}:${nodeId}`;
  const [selectedCategory, setSelectedCategory] = useState(() =>
    getActiveUpload(uploadKey) ? "Upload" : "Embed URL"
  );

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(FEATURES.UPLOAD_VIDEOS);

  const categoriesWithIcons = categories.map((category) => {
    const config = {
      label: category,
      value: category,
    };

    // gordon-0915: Record is an Essential-tier item exactly like Upload —
    // same feature key (FEATURES.UPLOAD_VIDEOS), same badge, same upgrade
    // card. The tier named on the badge is DERIVED from the entitlement the
    // gate returned — never a fixed string, or the badge upsells a plan
    // that doesn't unlock uploads.
    if ((category === "Upload" || category === "Record") && !isEligible) {
      config.customIcon = (
        <TierFeatureBadge
          requiredSubscription={requiredSubscription}
          subscription={subscription}
          placement="bottom"
        />
      );
    }

    return config;
  });

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? margin[side] + 1 : margin[side] - 1;
    if (newValue >= 0) handleChange("margin", newValue, side);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  // Update default props in reducer when settings change
  const updateDefaultVideoProps = (updatedProps) => {
    const updatedVideoProps = {
      ...videoProps,
      ...updatedProps,
    };
    dispatch(updateVideoStyles(updatedVideoProps));
  };

  const handleChange = (type, value, side) => {
    setProp((props) => {
      switch (type) {
        case "width":
          props.width = sliderToPercentage(value);
          updateDefaultVideoProps({ width: sliderToPercentage(value) });
          break;
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
            const updatedMargin = { ...props.margin };
            updateDefaultVideoProps({ margin: updatedMargin });
          }
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          updateDefaultVideoProps({ allSideMargin: value });
          break;
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          updateDefaultVideoProps({
            isApplyMarginOnAllSides: !props.isApplyMarginOnAllSides,
          });
          break;
        case "border":
          props.borderRadius = value;
          updateDefaultVideoProps({ borderRadius: value });
          break;
        case "borderColor":
          props.borderColor = value;
          updateDefaultVideoProps({ borderColor: value });
          break;
        case "resetColor":
          props.borderColor = null;
          updateDefaultVideoProps({ borderColor: null });
          break;
        case "borderWidth":
          props.borderWidth = value;
          updateDefaultVideoProps({ borderWidth: value });
          break;
        case "video":
          applyVideoMediaProps(props, value);
          break;
        case "title":
          props.title = value;
          updateDefaultVideoProps({ title: value });
          break;
        case "showVideoTitle":
          props.showVideoTitle = !props.showVideoTitle;
          updateDefaultVideoProps({ showVideoTitle: !props.showVideoTitle });
          break;
        case "showHyperlink":
          props.showHyperlink = !props.showHyperlink;
          updateDefaultVideoProps({ showHyperlink: !props.showHyperlink });
          break;
        case "hyperlink":
          props.hyperlink = value;
          updateDefaultVideoProps({ hyperlink: value });
          break;
        case "dropShadowColor":
          props.dropShadowColor = value;
          updateDefaultVideoProps({ dropShadowColor: value });
          break;
        case "resetShadow":
          props.dropShadowColor = null;
          updateDefaultVideoProps({ dropShadowColor: null });
          break;
        case "videoRemove":
          props.src = "";
          props.thumbnail = "";
      }
    }, 500);
  };

  // T22.91: an upload/recording resolves asynchronously — the user may have
  // opened a different Segment by then, and this craft `setProp` closure would
  // then write into a node that is no longer in the editor (silently lost,
  // leaving the block stuck on "Loading video"). Route the result to the
  // Segment it was started in; see writeVideoMedia.js.
  const handleAsyncVideoMedia = (value) =>
    dispatch(
      writeVideoMediaToOwningSegment({
        originSegmentId: selectedSegment?.id ?? null,
        nodeId,
        value,
        applyLive: () => handleChange("video", value),
      })
    );

  const videoSelected = src && src !== ASSETS.segmentEditor.UploadVideo;

  const renderVideoSelection = () => {
    return (
      <>
        {videoSelected && (
          <UploadedMedia
            type="video"
            media={src}
            title={title}
            onChange={(val) => handleChange("title", val)}
            showTitle={showVideoTitle}
            handleCheckbox={() => handleChange("showVideoTitle")}
            handleEditMedia={(value) => handleChange("video", value)}
            handleMediaRemove={() => handleChange("videoRemove")}
          />
        )}

        <Tabs
          categoriesWithIcons={categoriesWithIcons}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          tabStyle={{ maxWidth: 120 }}
          // T22.146: wrap instead of hiding the first tab (Upload) behind a
          // scroll chevron in the narrow pane — same strip as MediaSelection.
          sx={SOURCE_STRIP_SX}
        />
        <MediaLibraryReuse
          acceptedTypes={["video"]}
          segmentName={segmentName}
          contextLabel="a video"
          onInsert={(value) => handleChange("video", value)}
        />
        {selectedCategory === "Upload" && (
          <UploadMedia
            type="video"
            src={src}
            uploadKey={uploadKey}
            handleMedia={handleAsyncVideoMedia}
            handleTitle={(val) => handleChange("title", val)}
          />
        )}
        {MEDIA_RECORDER_ENABLED &&
          selectedCategory === "Record" &&
          (isCheckingEligibility ? (
            <CircularProgress />
          ) : isEligible ? (
            <MediaRecorder
              mode="video"
              onComplete={handleAsyncVideoMedia}
            />
          ) : (
            // h5718ffb7: Record is a paywall-bypass of Upload's own
            // FEATURES.UPLOAD_VIDEOS gate (UploadMedia/index.jsx) unless it is
            // gated identically — same feature key, same upsell card.
            <FeatureEligibility
              subscription={subscription}
              requiredSubscription={requiredSubscription}
            />
          ))}
        {selectedCategory === "Embed URL" && (
          <EmbedURL
            type="video"
            onConfirm={(value) => handleChange("video", value)}
          />
        )}
        {selectedCategory === "Browse" && (
          <MediaBrowser
            mediaType={"video"}
            onConfirm={(value) => handleChange("video", value)}
          />
        )}
        {selectedCategory === "Recent" && (
          <RecentMedia
            type={"video"}
            onClickMedia={(value) => handleChange("video", value)}
          />
        )}
      </>
    );
  };

  const renderVideoLayout = () => {
    return (
      <>
        <MarginBox
          margin={margin}
          increaseMargin={(side) => handleAdjustment(side, "increase")}
          decreaseMargin={(side) => handleAdjustment(side, "decrease")}
          isApplyMarginOnAllSides={isApplyMarginOnAllSides}
          allSideMargin={allSideMargin}
          handleMarginCheckBox={() => handleChange("marginCheckbox")}
          handleSliderMarginChange={(value) =>
            handleChange("allSideMargin", value)
          }
          handleMarginChange={(side, value) =>
            handleChange("margin", value, side)
          }
        />
        <SettingsSlider
          title="Resizing"
          sliderValue={percentageToSlider(width)}
          onValueChange={(val) => handleChange("width", val)}
          min={50}
          max={100}
          valueLabel={"%"}
        />
        <SettingsSlider
          title="Corner Rounding"
          sliderValue={borderRadius}
          onValueChange={(val) => handleChange("border", val)}
          min={0}
        />

        <ColorPopup
          title="Border"
          background={borderColor}
          handleChangeBackgroundColor={(val) =>
            handleChange("borderColor", val)
          }
          resetBackgroundColor={() => handleChange("resetColor")}
          sliderBoxTitle={"Border Width"}
          sliderValue={borderWidth}
          sliderChange={(val) => handleChange("borderWidth", val)}
          sliderMinValue={0}
          sliderMaxValue={8}
          isHideGradient={true}
          sliderSymbol={"px"}
          showSuggestedColorPalette={true}
          isBorderSlider
        />

        <ColorPopup
          title="Drop Shadow"
          background={dropShadowColor}
          handleChangeBackgroundColor={(val) =>
            handleChange("dropShadowColor", val)
          }
          resetBackgroundColor={() => handleChange("resetShadow")}
          showSlider={false}
          showSuggestedColorPalette={true}
          isHideGradient={true}
        />
      </>
    );
  };

  return (
    <>
      <Tabs
        categories={mainTabs}
        selectedCategory={selectedTab}
        onCategoryChange={(_, val) => setSelectedTab(val)}
        tabStyle={{ maxWidth: "50%" }}
        disabledCategories={src ? [] : [mainTabs[1]]}
      />
      <div style={{ marginBottom: 20 }} />
      {selectedTab === mainTabs[0] && renderVideoSelection()}
      {selectedTab === mainTabs[1] && renderVideoLayout()}
    </>
  );
};

export default VideoSettings;
